import React, { memo, useState, useRef, useEffect, useId } from 'react';
import { CmsImage } from 'common-ui';
import type { ImageEntryType } from 'tsconfig/types';

// --- YouTube iframe player ---
type YouTubeIframePlayerProps = {
  url: string;
  showThumbnail?: boolean;
  image?: ImageEntryType;
  onPlayPause?: () => void;
  onReadyFn?: (iframe: HTMLIFrameElement | null) => void;
  className?: string;
  testId?: string;
  loop?: boolean;
  width?: string;
  height?: string;
  controls?: boolean;
  lazy?: boolean;
};

const getYouTubeEmbedUrl = (url: string, controls: boolean, loop: boolean, autoplay: boolean) => {
  if (!url) {
    return '';
  }
  let videoId = '';
  const ytRegex = [
    /youtube\.com\/(?:watch\?v=|embed\/)([\w-]{11})/,
    /youtu\.be\/([\w-]{11})/,
    /youtube-nocookie\.com\/embed\/([\w-]{11})/,
  ];
  for (const re of ytRegex) {
    const match = re.exec(url);
    if (match) {
      [, videoId] = match;
      break;
    }
  }
  if (!videoId) {
    const vParam = /[?&]v=([\w-]{11})/.exec(url);
    if (vParam) {
      [, videoId] = vParam;
    }
  }
  if (videoId) {
    let embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0`;
    embedUrl += controls ? '&controls=1' : '&controls=0';
    if (autoplay) {
      embedUrl += '&autoplay=1';
    }
    if (loop) {
      embedUrl += `&loop=1&playlist=${videoId}`;
    }
    return embedUrl;
  }

  return null;
};

const YouTubeIframePlayer = memo(
  ({
    url,
    showThumbnail,
    image,
    onPlayPause,
    onReadyFn,
    className,
    testId,
    loop,
    width,
    height,
    controls = true,
    lazy = true,
  }: YouTubeIframePlayerProps) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const finalUrl = getYouTubeEmbedUrl(url, controls, !!loop, isPlaying);
    const id = useId();

    const thumbnailCache = useRef<boolean | undefined>(showThumbnail);

    const style: React.CSSProperties = {
      width: width || '100%',
      height: height || '100%',
      aspectRatio: '16/9',
      maxWidth: '100%',
      border: 'none',
      display: isPlaying ? 'block' : 'none',
    };

    const iframeProps: React.IframeHTMLAttributes<HTMLIFrameElement> = {
      src: isPlaying && finalUrl ? finalUrl : undefined,
      allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
      allowFullScreen: true,
      style,
      title: `Video Player ${testId || id}`,
      loading: lazy ? 'lazy' : undefined,
      sandbox: 'allow-scripts allow-same-origin allow-presentation',
      referrerPolicy: 'strict-origin-when-cross-origin',
      className,
      onLoad: () => {
        if (onReadyFn) {
          onReadyFn(iframeRef.current);
        }
      },
    };

    const handlePlay = () => {
      setIsPlaying(true);
      if (onPlayPause) {
        onPlayPause();
      }
    };

    useEffect(() => {
      if (thumbnailCache.current === true && thumbnailCache.current !== showThumbnail) {
        setIsPlaying(true);
        thumbnailCache.current = false;
      }
    }, [showThumbnail]);

    return (
      <div
        style={{
          // position: 'relative',
          width: width || '100%',
          maxWidth: '100%',
        }}
        className={className}
        data-testid={testId}
      >
        {showThumbnail && image && !isPlaying && (
          <button
            type="button"
            aria-label="Play video"
            onClick={handlePlay}
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              outline: 'none',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
            }}
          >
            <CmsImage contentItem={image} />
          </button>
        )}
        {isPlaying && <iframe {...iframeProps} title={`Video Player ${testId || id}`} />}
      </div>
    );
  },
);

export default YouTubeIframePlayer;
